import { Link } from 'react-router-dom';
import type { DateInvite } from '@/api/types';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { useNow } from '@/components/ui/Countdown';
import { isLobbyable, otherPerson, whenLabel } from './share';
import { needsTimes } from './PlannerDeck';
import { RunningLate } from '@/features/late/RunningLate';

/** The next accepted date worth surfacing on Home (scheduled within 24 h, or a "now" date). */
export function nextDate(list: DateInvite[], now = Date.now()): DateInvite | null {
  const upcoming = list
    .filter((i) => isLobbyable(i) && !i.missed && (!i.starts_at || Date.parse(i.starts_at) - now < 24 * 3600_000))
    .sort((a, b) => Date.parse(a.starts_at ?? a.created_at) - Date.parse(b.starts_at ?? b.created_at));
  return upcoming[0] ?? null;
}

/** v6.2: an invite waiting on me — the host picks a time, or the guest plans. */
export function yourMove(list: DateInvite[]): DateInvite | null {
  return list.find((i) => (i.role === 'host' && i.status === 'awaiting_host') || needsTimes(i)) ?? null;
}

/** "Your date with Maya starts in 15 min" / "…is on now" (SPEC_V6_1 §5). */
export function reminderLine(inv: DateInvite, now = Date.now()): string {
  const name = otherPerson(inv)?.display_name ?? 'your date';
  if (inv.session_id) return `Your date with ${name} is on`;
  if (!inv.starts_at) return `${name} said yes — meet in the lobby`;
  const mins = Math.round((Date.parse(inv.starts_at) - now) / 60_000);
  if (mins <= 0) return `Your date with ${name} is starting`;
  if (mins < 60) return `Your date with ${name} starts in ${mins} min`;
  return `Your date with ${name} — ${whenLabel(inv.starts_at, new Date(now)).toLowerCase()}`;
}

/** Home's "next thing": a date that's on soon (with running-late news under it). */
export function UpcomingCard({ inv }: { inv: DateInvite }) {
  const now = useNow(30_000);
  const other = otherPerson(inv);
  return (
    <>
      <Link
        to={`/dates/${inv.id}`}
        className="flex items-center gap-3 rounded-card bg-surface p-3.5 transition-colors hover:bg-raised focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg"
        data-testid="upcoming-date"
      >
        <Avatar avatar={other?.avatar} size={44} />
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold leading-snug">{reminderLine(inv, now)}</p>
          <p className="text-[13px] tabular-nums text-muted">Lobby’s open, hop in · {inv.length_min} min</p>
        </div>
        <Icon name="chevron-right" size={18} className="text-fg" />
      </Link>
      <RunningLate inv={inv} variant="card" />
    </>
  );
}

/** v6.2: an invite waiting on me — the host picks a time, or the guest plans. */
export function YourMoveCard({ inv }: { inv: DateInvite }) {
  const name = otherPerson(inv)?.display_name ?? 'They';
  return (
    <Link
      to={inv.role === 'host' ? '/friends#dates' : `/dates/${inv.id}`}
      className="flex items-center gap-3 rounded-card bg-surface p-3.5 transition-colors hover:bg-raised focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg"
      data-testid="dates-your-move"
    >
      <Avatar avatar={otherPerson(inv)?.avatar} size={44} />
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold leading-snug">{inv.role === 'host' ? `${name} planned your date — pick a time` : `Plan your date with ${name}`}</span>
        <span className="block text-[13px] text-muted">{inv.role === 'host' ? 'One tap and it’s on' : 'Vibe, times, a prop. 30 seconds.'}</span>
      </span>
      <Icon name="chevron-right" size={18} className="text-fg" />
    </Link>
  );
}
